import React from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle2, 
  ArrowLeft, 
  Calendar, 
  Info, 
  Bell, 
  Camera, 
  ShieldAlert, 
  PhoneCall, 
  Video, 
  Layers, 
  Smartphone,
  Check
} from 'lucide-react';
import useDocumentMetadata from '../hooks/useDocumentMetadata';

const FlexSmartApp = () => {
  useDocumentMetadata({
    title: 'FlexSmart App | Nem påmindelse om medicin | MedicCare',
    description: 'FlexSmart App er en påmindelsesapp, der hjælper borgere med at huske deres medicin. Henter data direkte fra FlexSuite og integrerer med kommunens systemer.'
  });

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-slate-900 py-20 lg:py-32 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/flexsmartapp-ui.png" 
            alt="FlexSmart App Hero" 
            className="w-full h-full object-cover opacity-30 blur-sm"
          />
          <div className="absolute inset-0 bg-[#2c3223]/85 mix-blend-multiply"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <Link 
              to="/medimi-smart" 
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-soft hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft size={14} /> Tilbage til Produkter
            </Link>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6">
              FlexSmart App
            </h1>
            <p className="text-lg md:text-xl text-slate-200 mb-8 leading-relaxed">
              En App, der påminder borgeren om at huske at tage sin medicin. Til dem som bare skal have lidt struktur i hverdagen og har et behov for påmindelser.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="/#kontakt" 
                className="bg-white text-[#2c3223] text-xs font-bold uppercase tracking-widest px-8 py-4 rounded-md hover:bg-brand-soft transition-all"
              >
                Anmod om demo
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="absolute -inset-4 bg-brand-soft/50 rounded-3xl rotate-2 z-0"></div>
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/chatgpt-image-6-jul-2026-13-03-57.png" 
                alt="FlexSmart App Interface" 
                className="rounded-2xl shadow-2xl relative z-10 max-w-md mx-auto w-full h-auto"
              />
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-block px-4 py-1.5 mb-6 text-[10px] font-bold uppercase tracking-[0.2em] bg-brand-soft text-[#2c3223] rounded-full">
                FlexSmart App
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6 leading-tight">
                Enkel og rettidig påmindelse
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                FlexSmart er en påmindelsesapp, hvor kvitteringer og alarmhåndtering håndteres effektivt. Appen henter automatisk medicininformation og doseringstidspunkter direkte fra Flex-systemet, og påminder borgeren om, hvornår det er tid til at tage sin medicin.
              </p>
              <p className="text-slate-600 mb-6 leading-relaxed">
                FlexSmart App kan tilsluttes Flex-systemet, der deler den samme stærke og sikre platform som vores TIM-medicinrobot. Takket være denne tætte integration sker ændringer fra lægens eller kommunens system helt automatisk og flyder direkte ud til borgerens FlexSmart App.
              </p>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Appen indeholder en overskuelig kalenderfunktion og detaljeret medicininformation, som sikrer, at borgeren altid har det fulde overblik over sin behandling.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="text-brand shrink-0 mt-1" size={18} />
                  <span className="text-sm font-medium text-slate-700">Fuld integration til FlexSuite</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="text-brand shrink-0 mt-1" size={18} />
                  <span className="text-sm font-medium text-slate-700">Kalender & medicininfo</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="text-brand shrink-0 mt-1" size={18} />
                  <span className="text-sm font-medium text-slate-700">Kvittering & alarmhåndtering</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="text-brand shrink-0 mt-1" size={18} />
                  <span className="text-sm font-medium text-slate-700">Deler platform med TIM robotten</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Options / Add-ons Section */}
      <section className="py-20 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block px-4 py-1.5 mb-4 text-[10px] font-bold uppercase tracking-[0.2em] bg-brand-soft text-[#2c3223] rounded-full">
              Skræddersyede løsninger
            </div>
            <h2 className="text-3xl font-extrabold text-slate-900 mb-4">Mulighed for tilkøb af optioner</h2>
            <p className="text-slate-600">
              FlexSmart App kan tilpasses præcis til den enkelte borgers eller kommunes behov med en række smarte tilkøbsmuligheder.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Option 1 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 flex flex-col hover:shadow-md transition-shadow">
              <div className="bg-brand-soft p-4 rounded-xl inline-flex self-start text-[#2c3223] mb-6">
                <Bell size={24} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Elektronisk / Personlig påmindelse</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Få ekstra struktur og tryghed i hverdagen med avancerede elektroniske påmindelser eller personligt tilpassede beskeder, der sikrer, at medicinen tages korrekt.
              </p>
            </div>

            {/* Option 2 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 flex flex-col hover:shadow-md transition-shadow">
              <div className="bg-brand-soft p-4 rounded-xl inline-flex self-start text-[#2c3223] mb-6">
                <Camera size={24} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Foto af piller / Skan dosisposer</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Mulighed for at se billeder af pillerne direkte i appen eller skanne dosisposerne for at verificere, at det er den korrekte medicin, der indtages.
              </p>
            </div>

            {/* Option 3 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 flex flex-col hover:shadow-md transition-shadow">
              <div className="bg-brand-soft p-4 rounded-xl inline-flex self-start text-[#2c3223] mb-6">
                <ShieldAlert size={24} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Send alarm til 3. part</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Sikkerhed for, at der reageres hurtigt. Hvis en påmindelse overses, kan appen automatisk sende en alarm videre til pårørende eller eksternt sundhedspersonale.
              </p>
            </div>

            {/* Option 4 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 flex flex-col hover:shadow-md transition-shadow">
              <div className="bg-brand-soft p-4 rounded-xl inline-flex self-start text-[#2c3223] mb-6 w-14 h-14 items-center justify-center">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/support.png" 
                  alt="Sundhedssupport" 
                  className="w-8 h-8 object-contain" 
                />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Sundhedssupport (kl. 06-23)</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Adgang til professionel sundhedsfaglig support i tidsrummet 06:00 til 23:00, så borgeren eller pårørende altid kan få hjælp og svar på spørgsmål.
              </p>
            </div>

            {/* Option 5 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 flex flex-col hover:shadow-md transition-shadow">
              <div className="bg-brand-soft p-4 rounded-xl inline-flex self-start text-[#2c3223] mb-6">
                <Video size={24} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Videosamtale med borger</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Mulighed for direkte videosamtaler mellem borgeren og sundhedspersonalet direkte i appen, hvilket muliggør virtuel omsorg og hurtig visuel afklaring.
              </p>
            </div>

            {/* Option 6 (Platform integration info) */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 flex flex-col hover:shadow-md transition-shadow">
              <div className="bg-brand-soft p-4 rounded-xl inline-flex self-start text-[#2c3223] mb-6 w-14 h-14 items-center justify-center">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/flexsuite.png" 
                  alt="FlexSuite" 
                  className="w-8 h-8 object-contain" 
                />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Fælles Platform</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Da appen deler platform med TIM-robotten og er fuldt integreret med FlexSuite, kan nye funktioner og optioner løbende tilføjes uden besvær.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Integration & Safety details */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block px-4 py-1.5 mb-6 text-[10px] font-bold uppercase tracking-[0.2em] bg-brand-soft text-[#2c3223] rounded-full">
                Sikkerhed & Integration
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6 leading-tight">
                Direkte synkronisering fra læge og kommune
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Takket være den direkte integration med kommunernes fagsystemer og det Fælles Medicinkort (FMK) / Lokalt Medicinkort (LMK), sikrer FlexSmart App, at borgeren altid har opdaterede oplysninger.
              </p>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Når lægen eller den kommunale sygeplejerske foretager ændringer i medicineringen eller doseringstidspunkterne, sendes disse opdateringer øjeblikkeligt gennem FlexSuite og direkte ud til appen. Det minimerer risikoen for fejlmedicinering og sparer værdifuld tid for personalet.
              </p>
            </div>
            <div className="bg-slate-50 p-8 lg:p-12 rounded-3xl border border-slate-100">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Hvorfor vælge FlexSmart App?</h3>
              <ul className="space-y-4">
                {[
                  'Henter automatisk medicininformation og doseringstidspunkter fra Flex.',
                  'Sikrer rettidige påmindelser, der hjælper til en struktureret hverdag.',
                  'Deler platform med TIM robotten for maksimal synergi.',
                  'Mulighed for avancerede tilkøb som sundhedssupport og videosamtaler.',
                  'Fuld integration med lægens og kommunens eksisterende systemer.'
                ].map((benefit, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <CheckCircle2 size={18} className="text-brand shrink-0 mt-1" />
                    <span className="text-slate-700 text-sm leading-relaxed">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#2c3223] text-white py-16">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Klar til at høre mere om FlexSmart App?</h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
            Kontakt os i dag for at høre mere om, hvordan FlexSmart App kan skabe struktur og tryghed i medicinhåndteringen.
          </p>
          <div className="flex justify-center gap-4">
            <a 
              href="/#kontakt" 
              className="bg-white text-[#2c3223] font-bold text-xs uppercase tracking-widest px-8 py-4 rounded-full hover:bg-brand-soft transition-colors"
            >
              Kontakt os nu
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FlexSmartApp;
